import {
  GAME_PICTURE_TYPES,
  MAX_GAME_PICTURE_BYTES,
  MIN_GAME_PICTURE_PIXELS,
  versionedIconUrl,
  type CatalogueWriteResult,
  type GamePictureType,
} from '@dayon/contracts';
import { movesSentence } from './catalogue-write.ts';

/**
 * A GAME'S OWN PICTURE, from the console's side: every decision the Picture
 * section in Edit configuration makes, and every sentence it states.
 *
 * Kept out of the component for the reason `provider-status.ts` is, and one
 * more: the back office has no component-test tooling, so a decision made in
 * markup is a decision nothing pins. The section draws what these return.
 *
 * ── WHERE THE PICTURE COMES FROM ─────────────────────────────────────────
 *
 * `game.game.icon_source` NULL means no operator has touched the picture;
 * PROVIDER means an operator went back to the provider's; UPLOADED means an
 * upload is what players see. NULL and PROVIDER both show the provider's
 * picture, or initials when it has none. `icon_url` is always the one players
 * see; `provider_icon_url` is the provider's, kept aside while an upload shows,
 * which is what "Use the provider's picture again" brings back.
 *
 * ── NOTHING HERE ASKS THE SERVER ANYTHING ────────────────────────────────
 *
 * A picture is STAGED in the dialog and written by its one Save, under the one
 * reason the dialog already asks for. Choosing a file reads it in this browser
 * only; the upload link, the upload and the confirm all wait for Save.
 */

/** A picture chosen but not yet saved, or a return to the provider's picture not yet saved. */
export type StagedPicture =
  | { kind: 'UPLOAD'; file: File; contentType: GamePictureType; previewUrl: string; advice: string[] }
  | { kind: 'REVERT' };

type Refusal = { ok: false; title: string; message: string };

/** "2 MB", from the contract's ceiling, so the words cannot drift from the number refused. */
const LIMIT_WORDS = `${String(MAX_GAME_PICTURE_BYTES / (1024 * 1024))} MB`;
const MIN = String(MIN_GAME_PICTURE_PIXELS);
const NOTHING_UPLOADED = 'Nothing was uploaded.';

const isPictureType = (type: string): type is GamePictureType =>
  (GAME_PICTURE_TYPES as readonly string[]).includes(type);

/**
 * A chosen file, judged by what the browser says about it before it is read.
 *
 * In this order, so the most useful refusal wins: an SVG is named as an SVG
 * whether its type or only its name says so (a browser can report an empty type
 * for one), then any other type, then an empty file, then one over the ceiling.
 * The server refuses all of these again; this is so an operator hears it before
 * anything is sent.
 */
export function checkPictureFile(file: {
  name: string;
  type: string;
  size: number;
}): { ok: true; contentType: GamePictureType } | Refusal {
  if (file.type === 'image/svg+xml' || /\.svg$/i.test(file.name)) {
    return {
      ok: false,
      title: 'SVG pictures are not accepted',
      message: `An SVG can carry a script, and game pictures are shown to players. Use a PNG, JPEG or WebP picture. ${NOTHING_UPLOADED}`,
    };
  }
  if (!isPictureType(file.type)) {
    return {
      ok: false,
      title: 'That file type is not accepted',
      message: `Use a PNG, JPEG or WebP picture. ${NOTHING_UPLOADED}`,
    };
  }
  if (file.size === 0) {
    return { ok: false, title: 'That file is empty', message: `It holds no picture. ${NOTHING_UPLOADED}` };
  }
  if (file.size > MAX_GAME_PICTURE_BYTES) {
    return {
      ok: false,
      title: 'That picture is too large',
      message: `The limit is ${LIMIT_WORDS}. Save it smaller, or as a JPEG or WebP, and choose it again. ${NOTHING_UPLOADED}`,
    };
  }
  return { ok: true, contentType: file.type };
}

/**
 * A chosen file once the browser has tried to read it as a picture.
 *
 * `null` is a file the browser could not read: a renamed text or HTML file
 * carries a picture's name and type, passes every check above, and is caught
 * here. Size is ADVICE, not a refusal — a small picture works, it only looks
 * soft on the tile — so it never stops the upload.
 */
export function checkPictureDecoded(
  decoded: { width: number; height: number } | null,
): { ok: true; advice: string[] } | Refusal {
  if (decoded === null || decoded.width < 1 || decoded.height < 1) {
    return {
      ok: false,
      title: 'That file could not be read as a picture',
      message: `It may be damaged, or it may be another kind of file with a picture's name. ${NOTHING_UPLOADED}`,
    };
  }
  const advice: string[] = [];
  if (decoded.width < MIN_GAME_PICTURE_PIXELS || decoded.height < MIN_GAME_PICTURE_PIXELS) {
    advice.push(
      `This picture is ${String(decoded.width)} × ${String(decoded.height)} pixels. Below ${MIN} × ${MIN} it may look blurred on the tile.`,
    );
  }
  return { ok: true, advice };
}

/**
 * The spec's sentence for JILI, verbatim. JILI's game list carries no artwork,
 * so its import writes neither picture column (pinned by the JILI import's own
 * test, which this sentence depends on).
 */
const JILI_LINE =
  'JILI publishes no pictures through its connection, so JILI tiles show a monogram until you upload one.';

const NOT_READY_SENTENCE =
  'Picture uploads are not switched on for this installation yet, so a picture cannot be uploaded. ' +
  'Whoever runs the platform switches them on. ' +
  "Going back to the provider's picture still works.";

/**
 * What the section says about the picture players see now, where the provider's
 * comes from, and what going back would show.
 *
 * `showing` follows what the tile draws: an upload, else whatever address is
 * there (the provider's), else initials. Going back is offered only while an
 * upload shows; NULL and PROVIDER are already the provider's picture. The revert
 * sentence says before Save what the tile will show after it — the initials, by
 * name, when the provider has no picture for this game.
 *
 * The provider line is about the DRIVER, as the read-only name rule is: SA's
 * hourly import sends a picture with most tables and keeps it current without
 * replacing an upload; JILI's sends none; the mock driver and a driver with no
 * import write nothing, so a picture is there only if one was put there.
 */
export function pictureSummary(input: {
  providerName: string;
  driver: string;
  iconSource: 'PROVIDER' | 'UPLOADED' | null;
  iconUrl: string | null;
  providerIconUrl: string | null;
  uploadsReady: boolean;
}): {
  showing: 'UPLOADED' | 'PROVIDER' | 'INITIALS';
  sentence: string;
  providerLine: string;
  canRevert: boolean;
  revertSentence: string | null;
  notReadySentence: string | null;
} {
  const { providerName, driver, iconSource, iconUrl, providerIconUrl, uploadsReady } = input;
  const showing = iconUrl === null ? 'INITIALS' : iconSource === 'UPLOADED' ? 'UPLOADED' : 'PROVIDER';
  const sentence =
    showing === 'UPLOADED'
      ? 'Players see a picture uploaded here.'
      : showing === 'PROVIDER'
        ? `Players see ${providerName}'s picture.`
        : "Players see the game's initials.";

  let providerLine: string;
  if (driver === 'JILI') providerLine = JILI_LINE;
  else if (driver === 'SA')
    providerLine =
      `${providerName} sends a picture with most of its tables. ` +
      'Its hourly import keeps that picture up to date and never replaces one uploaded here.';
  else if (providerIconUrl === null)
    providerLine = `${providerName} publishes no pictures, so its tiles show the game's initials until you upload one.`;
  else providerLine = `${providerName} has a picture of its own for this game. No import changes it.`;

  const canRevert = iconSource === 'UPLOADED';
  const revertSentence = !canRevert
    ? null
    : providerIconUrl !== null
      ? `Players will see ${providerName}'s picture again.`
      : `Players will see the game's initials again: ${providerName} has no picture for this game.`;

  return {
    showing,
    sentence,
    providerLine,
    canRevert,
    revertSentence,
    notReadySentence: uploadsReady ? null : NOT_READY_SENTENCE,
  };
}

/** The guidance under the preview, one line each. */
export const PICTURE_GUIDANCE = [
  "Players see this picture on the game's tile in the player app.",
  'The tile shows its middle, 4 wide by 3 tall.',
  `Use a square picture at least ${MIN} × ${MIN} pixels: PNG, JPEG or WebP, up to ${LIMIT_WORDS}. SVG is not accepted.`,
  'An uploaded picture stays until you change it here.',
] as const;

/** What a screen reader hears for the preview: the picture, by its game. */
export const pictureAlt = (gameName: string): string => `The picture for ${gameName}`;
/** The hidden file input's name, which the Upload button opens. */
export const PICTURE_INPUT_LABEL = 'Choose a picture file';

/**
 * Whether a chosen file, now read, may be staged. Reading takes a moment, so
 * two quick picks can finish out of order, and one can finish after Save was
 * pressed or after the dialog closed. Only the LATEST pick stages (an earlier one
 * finishing late must not replace it), never while saving (Save has already
 * taken what was staged), and never into a closed dialog. A pick refused here
 * shows nothing either: its refusal would describe a file nobody is looking at.
 */
export const pickMayStage = (input: {
  pick: number;
  latestPick: number;
  saving: boolean;
  open: boolean;
}): boolean => input.pick === input.latestPick && !input.saving && input.open;

/** The preview box with no picture to show. */
export const PICTURE_NONE = "No picture — players see the game's initials";
/** The preview box when the picture's address did not load. */
export const PICTURE_BROKEN = 'This picture could not be loaded.';
/** Under a chosen file. */
export const STAGED_UPLOAD_SENTENCE = 'New picture — it replaces the current one when you press Save.';
/** After the revert sentence, once going back is staged. Leading space: it continues that sentence's line. */
export const STAGED_REVERT_WHEN = ' This happens when you press Save.';

/**
 * Where the sentence for going back shows. Every action states what it will do
 * BEFORE it is pressed, so while nothing is staged the sentence sits beside
 * "Use the provider's picture again" as its hint. Once pressed it moves into the
 * staged notice and says it happens on Save, and the button it described is gone.
 * Nowhere while a new picture is staged, or when there is no upload to leave.
 */
export function revertWords(
  summary: { canRevert: boolean; revertSentence: string | null },
  staged: StagedPicture | null,
): { buttonHint: string | null; stagedNotice: string | null } {
  if (summary.revertSentence === null) return { buttonHint: null, stagedNotice: null };
  if (staged?.kind === 'REVERT') {
    return { buttonHint: null, stagedNotice: summary.revertSentence + STAGED_REVERT_WHEN };
  }
  if (staged === null && summary.canRevert) return { buttonHint: summary.revertSentence, stagedNotice: null };
  return { buttonHint: null, stagedNotice: null };
}

/** The Games table's sub-line for a title showing an upload; nothing otherwise. */
export function pictureListTag(row: { iconSource: string | null | undefined }): string | null {
  return row.iconSource === 'UPLOADED' ? 'picture uploaded here' : null;
}

/**
 * What the preview box shows: a chosen file before anything else, then the
 * picture players see now. An upload is asked for by its versioned address, as
 * the player's tile asks for it; the provider's exactly as published. While
 * going back is only staged, the current picture still shows — the sentence
 * beside it says what Save will change.
 */
export function picturePreviewSource(
  game: { iconUrl: string | null; iconSource: string | null; iconUpdatedAt: string | null },
  staged: StagedPicture | null,
): string | null {
  if (staged?.kind === 'UPLOAD') return staged.previewUrl;
  return versionedIconUrl(game.iconUrl, game.iconSource, game.iconUpdatedAt);
}

/** The one Save counts every field that moved, and one more for a staged picture. */
export const saveCount = (movedFields: number, staged: StagedPicture | null): number =>
  movedFields + (staged === null ? 0 : 1);

/** What a good note looks like FOR THIS SAVE: a picture's reason when the picture is all that changes. */
export function notePlaceholder(movedFields: number, staged: StagedPicture | null): string {
  if (movedFields === 0 && staged !== null) return 'New tile artwork from the brand team, approved on 29 Oct.';
  return 'Certificate reads 96.00, not 96.50 — corrected against the PDF on file.';
}

/**
 * The browser's upload straight to storage, as a popup — or null when it arrived.
 *
 * `null` status is a PUT that threw: no answer at all. 412 is the upload link
 * refusing to overwrite: a link only creates, so a retry after a dropped
 * connection that had in fact landed is refused, and a fresh link is the cure.
 * Anything else is almost always the network at this end — the bytes go from
 * this browser to storage, never through the platform.
 */
export function storagePutRefusal(status: number | null): { title: string; message: string } | null {
  if (status !== null && status >= 200 && status < 300) return null;
  if (status === 412) {
    return {
      title: 'That upload link was already used',
      message: 'Nothing was changed. Choose the picture again to get a fresh link.',
    };
  }
  return {
    title: 'The picture did not reach storage',
    message:
      'Nothing was changed. The upload goes from this browser straight to storage, so this is usually a network problem at this end. Try again.',
  };
}

/**
 * The popup heading when the picture's write succeeded and the fields' write
 * that follows it was refused. The picture is not undone: it was saved under
 * its own reason row, and the server's sentence says why the rest was not.
 */
export const PICTURE_SAVED_FIELDS_NOT_TITLE = 'The picture was saved; the other changes were not';

/**
 * An answer that never came back (NETWORK: no connection) or could not be read
 * (MALFORMED_RESPONSE). The console's api client throws both as errors like any
 * refusal, but they are not refusals: the write may have landed before the
 * answer was lost, so no sentence may say it failed.
 */
const answerWasLost = (code: string | undefined): boolean =>
  code === 'NETWORK' || code === 'MALFORMED_RESPONSE';

/**
 * The popup when the picture saved and the fields' write that followed failed.
 * A refusal keeps the heading that says the other changes were not saved, under
 * the server's own sentence. A lost answer says they MAY have been: repeating a
 * change that did land is refused as moving nothing, which would read as a
 * second failure, so the sentence sends the operator to the log first. The
 * screen re-reads the log after this popup, because the picture did save.
 */
export function partialSaveMessage(error: { code: string | undefined; message: string }): {
  title: string;
  message: string;
} {
  if (answerWasLost(error.code)) {
    return {
      title: 'The picture was saved; the other changes may have been too',
      message:
        'No answer came back for the other changes, so they may have been saved. Check the Configuration log before making them again.',
    };
  }
  return { title: PICTURE_SAVED_FIELDS_NOT_TITLE, message: error.message };
}

/**
 * The popup when a Save of fields alone (no picture staged) got no answer — or
 * null for a refusal, which keeps the dialog's usual heading and the server's
 * sentence. "<name> was not saved" would be untrue: the edit may have landed.
 */
export function fieldsAnswerLost(code: string | undefined): { title: string; message: string } | null {
  if (!answerWasLost(code)) return null;
  return {
    title: 'No answer came back for the changes',
    message: 'The changes may have been saved. Check the Configuration log before trying again.',
  };
}

/**
 * The popup when the picture's own write (the confirm after an upload, or going
 * back) got no answer — or null for a refusal, which keeps the dialog's usual
 * heading and the server's sentence. The write may have committed, so the
 * sentence says so; nothing after it was sent, because Save stops there.
 */
export function pictureAnswerLost(code: string | undefined): { title: string; message: string } | null {
  if (!answerWasLost(code)) return null;
  return {
    title: 'No answer came back for the picture',
    message:
      'The picture may have changed, and nothing else was sent. Check the Configuration log before trying again.',
  };
}

/**
 * One result for the screen holding every move the Save made, named as the
 * last write named the game (a rename lands in the second write, after the
 * picture's), so the sentence under the table says everything that moved.
 */
export const combinedWrite = (
  results: readonly [CatalogueWriteResult, ...CatalogueWriteResult[]],
): CatalogueWriteResult => results.reduce((all, next) => ({ ...next, changed: [...all.changed, ...next.changed] }));

/**
 * The toast after Save: every move in the order made, as one sentence, so
 * where the reason is recorded is said once however many writes the Save made.
 */
export const savedSentence = (results: readonly [CatalogueWriteResult, ...CatalogueWriteResult[]]): string =>
  movesSentence(combinedWrite(results));
